<template>
    <div class="page-container">
        <FeatureGate featureKey="point_control">
        <div class="page-header-wrapper" :class="{ 'is-scrolled': isScrolled }">
            <div class="content-wrapper">
                <!-- Page Header -->
                <div class="page-header">
                    <div class="header-left">
                        <h1 class="page-title">{{ page.cardTitle }}</h1>
                        <p class="page-subtitle">Configure voucher details, limits, and expiration.</p>
                    </div>
                    <div class="header-actions">
                        <button type="button" class="btn-cancel" @click="cancel">Cancel</button>
                        <button type="button" class="btn-save" @click="change()" :disabled="page.isLoading">
                            <span v-if="page.isLoading"><i class="fas fa-spinner fa-spin me-2"></i> Saving...</span>
                            <span v-else><i class="fas fa-check-circle me-2"></i> Save Voucher</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="content-wrapper" v-if="isLoaded">
            <div class="main-grid">
                <!-- Left Column: Main Form -->
                <div class="grid-column main-col content-card">
                    <!-- Section: General Info -->
                    <div class="card-section">
                        <h3 class="section-title">General Info</h3>
                        <p class="section-desc">Basic information and type of the voucher.</p>

                        <div class="form-grid">
                            <div class="form-item form-row">
                                <div class="form-sub-item">
                                    <label>Name <span class="required">*</span></label>
                                    <o-input-text name="name" v-model="voucher.name" type="text" :autocomplete="'off'" maxlength="150"/>
                                    <div class="d-flex justify-content-end" style="display: flex; justify-content: flex-end; margin-top: 4px;">
                                        <small class="char-counter text-muted" style="font-size: 11px; color: #6c757d;">{{ (voucher.name || '').length }}/150 characters</small>
                                    </div>
                                    <div v-if="validation.name" class="error-text mt-1">{{ validation.name[0] }}</div>
                                </div>
                            </div>

                            <div class="form-item form-row">
                                <div class="form-sub-item">
                                    <label>Voucher Type <span class="required">*</span></label>
                                    <select v-model="voucher.voucher_type" class="modern-select">
                                        <option value="product_service">Product & Service</option>
                                        <option value="credit_discount">Credit Discount</option>
                                    </select>
                                    <div v-if="validation.voucher_type" class="error-text mt-1">{{ validation.voucher_type[0] }}</div>
                                </div>
                                <div class="form-sub-item">
                                    <label>Point Needed <span class="required">*</span></label>
                                    <o-input-text
                                        name="point"
                                        :autocomplete="'off'"
                                        v-model="voucher.point"
                                        type="number"
                                        @input="validateInput"
                                        @keydown="preventInvalidInput"
                                    />
                                    <div v-if="validation.point" class="error-text mt-1">{{ validation.point[0] }}</div>
                                    <div v-if="hasValidationErrors" class="error-text mt-1">
                                        <ul style="margin-bottom:0; padding-left:20px;">
                                            <li v-for="(message, index) in validationPoint" :key="index">{{ message }}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="form-item full">
                                <label>Description <span class="required">*</span></label>
                                <p class="helper-text mt-1 mb-2">
                                    This description will be displayed to users. You can use this space to outline the Terms and Conditions (T&C) for using this voucher.
                                </p>
                                <OTextEditor
                                    v-model="voucher.description"
                                    placeholder="Enter description of voucher..."
                                />
                                <div v-if="validation.description" class="error-text mt-1">{{ validation.description[0] }}</div>
                            </div>
                        </div>
                    </div>

                    <template v-if="selectedBusinessProfile.merchant_id == null">
                        <hr class="section-divider">

                        <!-- Section: Eligible Merchants (Collapsible & Default Collapsed) -->
                        <div class="card-section merchant-collapsible-section">
                            <div 
                                class="merchant-section-header d-flex justify-content-between align-items-center"
                                @click="isEligibleMerchantsCollapsed = !isEligibleMerchantsCollapsed"
                            >
                                <div class="d-flex align-items-center gap-2">
                                    <i class="fas fa-store section-header-icon me-2"></i>
                                    <div>
                                        <h3 class="section-title mb-1">Eligible Merchants <span class="required">*</span></h3>
                                        <p class="section-desc mb-0">Select which merchants are allowed to issue and accept this voucher.</p>
                                    </div>
                                </div>
                                <div class="d-flex align-items-center gap-3">
                                    <span class="merchant-badge-pill">
                                        {{ selectedMerchantCount }} / {{ totalMerchantCount }} Merchants Selected
                                    </span>
                                    <button type="button" class="btn-collapse-toggle">
                                        <i class="fas fa-chevron-down" :class="{ 'rotated': !isEligibleMerchantsCollapsed }"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Expandable Body -->
                            <div v-show="!isEligibleMerchantsCollapsed" class="merchant-section-body mt-3">
                                <!-- Controls Bar -->
                                <div class="merchant-control-bar">
                                    <!-- Search Input -->
                                    <div class="merchant-search-wrapper">
                                        <i class="fas fa-search search-icon"></i>
                                        <input 
                                            type="text" 
                                            v-model="merchantSearch" 
                                            class="merchant-search-input" 
                                            placeholder="Search merchant or branch..."
                                        />
                                    </div>

                                    <!-- Batch Select Buttons -->
                                    <div class="merchant-action-group">
                                        <button type="button" class="merchant-btn merchant-btn-primary" @click="selectAllMerchants">
                                            <i class="fas fa-check-double me-1"></i> Select All
                                        </button>
                                        <button type="button" class="merchant-btn merchant-btn-secondary" @click="deselectAllMerchants">
                                            <i class="fas fa-times me-1"></i> Clear All
                                        </button>
                                    </div>
                                </div>

                                <!-- Branch Groups Checklist UI -->
                                <div class="branches-merchant-wrapper">
                                    <div 
                                        v-for="group in filteredBranchGroups" 
                                        :key="group.branch_id || group.branch_name" 
                                        class="branch-card-container"
                                    >
                                        <div class="branch-card-header">
                                            <div class="branch-header-info">
                                                <i class="fas fa-code-branch branch-icon"></i>
                                                <strong class="branch-title-text">{{ group.branch_name }}</strong>
                                                <span class="branch-count-badge">
                                                    {{ getBranchSelectedCount(group) }} / {{ group.merchants.length }}
                                                </span>
                                            </div>
                                            <button 
                                                type="button" 
                                                class="branch-toggle-btn"
                                                @click="toggleBranchChecklist(group)"
                                            >
                                                <i :class="isBranchAllSelected(group) ? 'fas fa-check-square' : 'far fa-square'" class="me-1"></i>
                                                {{ isBranchAllSelected(group) ? 'Deselect Branch' : 'Select All in Branch' }}
                                            </button>
                                        </div>
                                        
                                        <div class="branch-card-body">
                                            <div class="merchant-grid-layout">
                                                <label 
                                                    v-for="merch in group.merchants" 
                                                    :key="merch.value" 
                                                    class="merchant-checkbox-card"
                                                    :class="{ 'merchant-card-selected': isMerchantSelected(merch.value) }"
                                                >
                                                    <input 
                                                        type="checkbox" 
                                                        class="merchant-input-checkbox"
                                                        :value="merch.value"
                                                        :checked="isMerchantSelected(merch.value)"
                                                        @change="toggleMerchantSelection(merch.value)"
                                                    />
                                                    <div class="merchant-info-content">
                                                        <div class="merchant-name-text">{{ merch.text }}</div>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="filteredBranchGroups.length === 0" class="merchant-empty-state">
                                        <i class="fas fa-store-slash empty-icon"></i>
                                        <div>No merchants found matching "{{ merchantSearch }}"</div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="validation.merchant_list || validation.merchant_id" class="error-text mt-2">
                                {{ (validation.merchant_list && validation.merchant_list[0]) || (validation.merchant_id && validation.merchant_id[0]) }}
                            </div>
                        </div>
                    </template>
                    
                    <template v-if="voucher.voucher_type === 'credit_discount'">
                        <hr class="section-divider">

                        <!-- Section: Discount Configuration -->
                        <div class="card-section">
                            <h3 class="section-title">Discount Configuration</h3>
                            <p class="section-desc">Set how the discount is calculated and its limits.</p>

                            <div class="form-grid">
                                <div class="form-item form-row">
                                    <div class="form-sub-item">
                                        <label>Discount Type <span class="required">*</span></label>
                                        <select v-model="voucher.discount_type" class="modern-select">
                                            <option value="amount">Fixed Amount</option>
                                            <option value="percentage">Percentage</option>
                                        </select>
                                        <div v-if="validation.discount_type" class="error-text mt-1">{{ validation.discount_type[0] }}</div>
                                    </div>
                                    <div class="form-sub-item">
                                        <label>
                                            Discount Value <span class="required">*</span>
                                            <span class="position-relative d-inline-flex align-items-center ms-1" @mouseenter="showDiscountValueTip = true" @mouseleave="showDiscountValueTip = false">
                                                <i class="fas fa-question-circle text-muted" style="cursor: help; font-size: 13px;"></i>
                                                <Transition name="fade">
                                                    <div v-if="showDiscountValueTip" class="date-selection-tip discount-value-tip">
                                                        <i class="fas fa-info-circle mt-1 flex-shrink-0"></i>
                                                        <span v-if="voucher.discount_type === 'amount'">Nominal amount in IDR (e.g. 50000 for Rp 50.000).</span>
                                                        <span v-else>Percentage (1-100). (e.g. 20 for 20%).</span>
                                                    </div>
                                                </Transition>
                                            </span>
                                        </label>
                                        <!-- Fixed Amount Input -->
                                        <input 
                                            v-if="voucher.discount_type === 'amount'"
                                            class="form-control" 
                                            name="discount_value" 
                                            autocomplete="off" 
                                            v-model="displayDiscountValue" 
                                            type="text" 
                                            placeholder="e.g. 50.000" 
                                            @input="e => validatePriceInput(e, 'discount_value')" 
                                            @keydown="preventInvalidInput" 
                                            @paste="e => handlePricePaste(e, 'discount_value')"
                                        />
                                        <!-- Percentage Input -->
                                        <o-input-text 
                                            v-else
                                            name="discount_value" 
                                            v-model="voucher.discount_value" 
                                            type="number" 
                                            :autocomplete="'off'"
                                            placeholder="e.g. 20"
                                            :max="100"
                                            :min="1"
                                        />
                                        <div v-if="validation.discount_value" class="error-text mt-1">{{ validation.discount_value[0] }}</div>
                                    </div>
                                </div>
                                
                                <div class="form-item form-row">
                                    <div class="form-sub-item">
                                        <label>Min Transaction</label>
                                        <input 
                                            class="form-control" 
                                            name="min_transaction" 
                                            autocomplete="off" 
                                            v-model="displayMinTransaction" 
                                            type="text" 
                                            placeholder="Leave empty for no minimum" 
                                            @input="e => validatePriceInput(e, 'min_transaction')" 
                                            @keydown="preventInvalidInput" 
                                            @paste="e => handlePricePaste(e, 'min_transaction')"
                                        />
                                        <div v-if="validation.min_transaction" class="error-text mt-1">{{ validation.min_transaction[0] }}</div>
                                    </div>
                                    <div class="form-sub-item" v-if="voucher.discount_type === 'percentage'">
                                        <label>Max Discount <span class="required">*</span></label>
                                        <input 
                                            class="form-control" 
                                            name="max_discount" 
                                            autocomplete="off" 
                                            v-model="displayMaxDiscount" 
                                            type="text" 
                                            placeholder="e.g. 50.000" 
                                            @input="e => validatePriceInput(e, 'max_discount')" 
                                            @keydown="preventInvalidInput" 
                                            @paste="e => handlePricePaste(e, 'max_discount')"
                                        />
                                        <div v-if="validation.max_discount" class="error-text mt-1">{{ validation.max_discount[0] }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>



                    <hr class="section-divider">

                    <!-- Section: Limits & Stock -->
                    <div class="card-section">
                        <h3 class="section-title">Limits & Stock</h3>
                        <p class="section-desc">Stock quantity and member tier restrictions.</p>

                        <div class="form-grid">
                            <div class="form-item full">
                                <label class="lock-tier-card" :class="{ active: voucher.is_limited }">
                                    <div class="lock-tier-info">
                                        <i class="fas fa-boxes lock-icon"></i>
                                        <div>
                                            <div class="lock-tier-title">Limited Stock</div>
                                            <div class="lock-tier-desc">Enable to limit voucher quantity.</div>
                                        </div>
                                    </div>
                                    <div class="lock-tier-toggle">
                                        <input type="checkbox" v-model="voucher.is_limited" class="toggle-input" />
                                        <div class="toggle-slider"></div>
                                    </div>
                                </label>
                            </div>
                            
                            <div class="form-item form-row">
                                <div class="form-sub-item" v-if="voucher.is_limited">
                                    <label>Stock Quantity <span class="required">*</span></label>
                                    <o-input-text name="stock" :autocomplete="'off'" v-model="voucher.stock" type="number" :required="true"/>
                                    <div v-if="validation.stock" class="error-text mt-1">{{ validation.stock[0] }}</div>
                                </div>

                                <div class="form-sub-item" v-if="selectedBusinessProfile.corporate_id != null">
                                    <label>Member Tier <span class="required">*</span></label>
                                    <o-lookup-member-tier 
                                        v-model="voucher.member_tier_id"
                                        show-all-option
                                        :corporate_id="selectedBusinessProfile.corporate_id" 
                                        class="modern-v-select"
                                        placeholder="-- Select Member Tier --"
                                    />
                                    <div v-if="validation.member_tier_id" class="error-text mt-1">{{ validation.member_tier_id[0] }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr class="section-divider">

                    <!-- Section: Validity -->
                    <div class="card-section">
                        <h3 class="section-title">Validity</h3>
                        <p class="section-desc">Expiration date and cancellation rules.</p>
                        
                        <div class="form-grid">
                            <div class="form-item form-row">
                                <div class="form-sub-item">
                                    <label>Expiration Date <span class="required">*</span></label>
                                    <DatePicker
                                        v-model="tempExpiration"
                                        :enableTime="true"
                                        placeholder="Select Expiration Date">
                                    </DatePicker>
                                    <div v-if="validation.expiration" class="error-text mt-1">{{ validation.expiration[0] }}</div>
                                </div>
                            </div>

                            <div class="form-item full mt-2">
                                <label class="lock-tier-card" :class="{ active: voucher.cancelable }">
                                    <div class="lock-tier-info">
                                        <i class="fas fa-undo lock-icon"></i>
                                        <div>
                                            <div class="lock-tier-title">Cancelable</div>
                                            <div class="lock-tier-desc">Allow users to cancel the voucher.</div>
                                        </div>
                                    </div>
                                    <div class="lock-tier-toggle">
                                        <input type="checkbox" v-model="voucher.cancelable" class="toggle-input" />
                                        <div class="toggle-slider"></div>
                                    </div>
                                </label>
                            </div>

                            <div class="form-item form-row" v-if="voucher.cancelable">
                                <div class="form-sub-item">
                                    <label>Cancellation Cutoff <span class="required">*</span></label>
                                    <div class="d-flex gap-2">
                                        <div style="flex:1;">
                                            <span class="text-muted" style="font-size:11px;">Month</span>
                                            <o-input-text name="cancellationmonth" v-model="cancellation_cutoff.month" type="number" :required="true"/>
                                        </div>
                                        <div style="flex:1;">
                                            <span class="text-muted" style="font-size:11px;">Day</span>
                                            <o-input-text name="cancellationday" v-model="cancellation_cutoff.day" type="number" min="0" max="30" :required="true"/>
                                        </div>
                                    </div>
                                    <div class="mt-2 text-muted" style="font-size: 11px;">
                                        <strong>Note:</strong> Cutoff cannot be the same as or later than expiration. E.g. cutoff of 2 days before an expiration on Dec 10 means cancelable before Dec 8.
                                    </div>
                                    <div v-if="validation.cancellation_cutoff || (error && error.toLowerCase().includes('cancellation cutoff'))" class="error-box-inline mt-3">
                                        <i class="fas fa-exclamation-triangle me-2"></i>
                                        <span>{{ validation.cancellation_cutoff ? validation.cancellation_cutoff[0] : error.replace('• ', '') }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Column: Sidebar -->
                <div class="grid-column side-col">
                    <!-- Avatar Card -->
                    <div class="content-card mb-4">
                        <h3 class="section-title">Voucher Image <span class="required">*</span></h3>
                        <p class="section-desc">Upload an image representation.</p>

                        <div class="visual-upload-section mt-3">
                            <o-image-input name="avatar" id="avatar" :imageurl="image.url" @selectImage="imageHandling"></o-image-input>
                        </div>
                        <div v-if="validation.avatar" class="error-text mt-2">{{ validation.avatar[0] }}</div>
                    </div>

                    <!-- Merchant Scope Summary Card -->
                    <div class="content-card mb-4" v-if="!selectedBusinessProfile.merchant_id">
                        <h3 class="section-title"><i class="fas fa-info-circle section-header-icon me-2"></i>Scope Summary</h3>
                        <p class="section-desc">Selected eligibility scope for this voucher.</p>

                        <div class="p-3 bg-light rounded-3 border mt-3">
                            <div class="d-flex align-items-center justify-content-between mb-2">
                                <span class="text-muted small">Scope Mode:</span>
                                <span class="scope-summary-badge">
                                    {{ voucher.merchant_scope === 'all_corporate' ? 'Corporate Voucher (Shared)' : 'Merchant Voucher (Single)' }}
                                </span>
                            </div>
                            <div class="d-flex align-items-center justify-content-between">
                                <span class="text-muted small">Selected Merchants:</span>
                                <strong class="text-dark">{{ selectedMerchantCount }} / {{ totalMerchantCount }}</strong>
                            </div>

                            <!-- Selected Merchants Checklist Summary -->
                            <div class="selected-merchants-summary-list mt-3 pt-3 border-top" v-if="selectedMerchantsList.length > 0">
                                <div class="text-muted small fw-semibold mb-2">Selected Merchants List:</div>
                                <div class="selected-merchant-items-container">
                                    <div 
                                        v-for="merch in selectedMerchantsList" 
                                        :key="merch.value" 
                                        class="selected-merchant-summary-item d-flex align-items-center gap-2 py-1"
                                    >
                                        <i class="fas fa-check-circle theme-check-icon"></i>
                                        <span class="merchant-summary-name text-truncate">{{ merch.text }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="selected-merchants-summary-list mt-3 pt-3 border-top text-center text-muted small" v-else>
                                <i class="fas fa-exclamation-circle me-1"></i> No merchants selected.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Global Loading Overlay -->
        <div v-if="page.isLoading" class="global-loader">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
        </FeatureGate>
    </div>
</template>

<script>
import { OInputText, OImageInput, OLookupMemberTier } from '@/components/OmnihControl';
import OTextEditor from '@/components/OmnihControl/OTextEditor.vue';
import { reactive, ref, onMounted, onUnmounted, watch, nextTick, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from '@/composables/useToast'
import store from '@/store'
import DatePicker from '@/components/DatePicker.vue';
import FeatureGate from '@/components/upsell/FeatureGate.vue';

export default {
name: 'AddVoucher',
components: {
    OInputText,
    OImageInput,
    OLookupMemberTier,
    OTextEditor,
    DatePicker,
    FeatureGate,
},
setup(){
    const page = reactive ({
        isLoading: false,
        cardTitle: 'Create Voucher'
    });
    const isLoaded = ref(false);
    const validationPoint = ref([]);
    const hasValidationErrors = computed(() => validationPoint.value.length > 0);

    const voucher = reactive({
        name: '',
        // point: 0,
        merchant_id: '',
        expiration: '',
        is_limited: false,
        stock: '',
        avatar: '',
        member_tier_id: '',
        description: '',
        cancelable: false,
        voucher_type: 'product_service',
        discount_type: 'amount',
        discount_value: '',
        min_transaction: '',
        max_discount: '',
        merchant_scope: 'all_corporate',
        merchant_list: []
    });

    const image = reactive({
        url: ""
    });

    const options = ref([]);

    const validation = ref([]);
    const error = ref('');
    
    const router = useRouter();

    const route = useRoute();
    const { showToast } = useToast();

    const merchants = ref([]);
    const isEligibleMerchantsCollapsed = ref(true);

    const tempExpiration = ref('');
    const showOkExpiration = ref(false);
    const showDiscountValueTip = ref(false);

    // Number Formatting Logic
    function formatNumber(num) {
        if (!num && num !== 0) return '';
        return Math.floor(Number(num)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    function parseNumber(str) {
        if (!str) return 0;
        return parseInt(str.toString().replace(/\./g, '').replace(/[^0-9]/g, '')) || 0;
    }

    const displayDiscountValue = computed({
        get() { return formatNumber(voucher.discount_value); },
        set(val) {
            const MAX_PRICE = 900000000;
            let parsed = parseNumber(val);
            voucher.discount_value = parsed > MAX_PRICE ? MAX_PRICE : parsed;
        }
    });

    const displayMinTransaction = computed({
        get() { return formatNumber(voucher.min_transaction); },
        set(val) {
            const MAX_PRICE = 900000000;
            let parsed = parseNumber(val);
            voucher.min_transaction = parsed > MAX_PRICE ? MAX_PRICE : (parsed === 0 && !val ? '' : parsed);
        }
    });

    const displayMaxDiscount = computed({
        get() { return formatNumber(voucher.max_discount); },
        set(val) {
            const MAX_PRICE = 900000000;
            let parsed = parseNumber(val);
            voucher.max_discount = parsed > MAX_PRICE ? MAX_PRICE : (parsed === 0 && !val ? '' : parsed);
        }
    });

    function validatePriceInput(event, field) {
        const MAX_PRICE = 900000000;
        let inputValue = event.target.value;
        let numericValue = inputValue.replace(/[^0-9]/g, '');
        if (numericValue.length > 9) numericValue = numericValue.substring(0, 9);
        let numericPrice = parseInt(numericValue) || 0;
        if (numericPrice > MAX_PRICE) numericPrice = MAX_PRICE;
        
        if (field === 'min_transaction' || field === 'max_discount') {
            voucher[field] = inputValue === '' ? '' : numericPrice;
        } else {
            voucher[field] = numericPrice;
        }
    }

    function preventInvalidInput(event) {
        const key = event.key;
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (allowedKeys.includes(key)) return true;
        if (event.ctrlKey || event.metaKey) return true;
        if (!/^[0-9]$/.test(key)) {
            event.preventDefault();
            return false;
        }
        return true;
    }

    function handlePricePaste(event, field) {
        event.preventDefault();
        const pastedData = (event.clipboardData || window.clipboardData).getData('text');
        let numericValue = pastedData.replace(/[^0-9]/g, '');
        if (numericValue.length > 9) numericValue = numericValue.substring(0, 9);
        const MAX_PRICE = 900000000;
        let numericPrice = parseInt(numericValue) || 0;
        if (numericPrice > MAX_PRICE) numericPrice = MAX_PRICE;
        voucher[field] = numericPrice;
    }

    const cancellation_cutoff = reactive({
        month: 0,
        day: 0
    });

    const selectedBusinessProfile = reactive({
        corporate_id: '',
        branch_id: '',
        merchant_id: ''
    })

    async function closeDatePicker(event) {
        event.target.blur();
    }

    async function onMountedAsync() {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    page.isLoading = true;

                    await loadBusinessProfile();
                    if (route.params.id) {
                        await loadState(route.params.id);
                    }
                    isLoaded.value = true;
                    page.isLoading = false;

                    resolve();
                } catch (error) {
                    reject(error);
                }
            })();
        });
    }

    const isScrolled = ref(false);

    const handleScroll = () => {
        isScrolled.value = window.scrollY > 10;
    };

    // Call the function
    onMounted(() => {
        window.scrollTo(0, 0);
        window.addEventListener('scroll', handleScroll);
        onMountedAsync();
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll);
    });

    async function loadState(id) {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    const data = await store.dispatch('Voucher/read_single', id);

                    voucher.id = data.id;
                    voucher.name = data.name;
                    voucher.point = data.point;
                    voucher.merchant_id = data.merchant_id;
                    voucher.is_limited = data.is_limited ? true : false;
                    voucher.stock = data.stock;
                    voucher.member_tier_id = data.member_tier_id ? data.member_tier_id : '-';
                    voucher.description = data.desc;
                    voucher.cancelable = data.cancelable === 1;
                    voucher.voucher_type = data.voucher_type ?? 'product_service';
                    voucher.discount_type = data.discount_type ?? 'amount';
                    voucher.discount_value = data.discount_value ?? '';
                    voucher.min_transaction = data.min_transaction ?? '';
                    voucher.max_discount = data.max_discount ?? '';
                    voucher.merchant_scope = data.merchant_scope ?? 'all_corporate';
                    voucher.merchant_list = data.allowed_merchants ?? [];

                    // MEM-1287: hanya untuk konteks corporate/branch (bukan merchant)
                    if (!selectedBusinessProfile.merchant_id) {
                        // Voucher lama 'all_corporate' dengan allowed_merchants kosong -> isi semua merchant
                        if (voucher.merchant_scope === 'all_corporate' && voucher.merchant_list.length === 0) {
                            voucher.merchant_list = merchants.value.map(m => m.value);
                        }
                        // Normalisasi scope voucher lama sesuai aturan jumlah merchant
                        syncMerchantScope();
                    }

                    voucher.expiration = new Date(data.expiration);
                    tempExpiration.value = new Date(data.expiration);

                    image.url = data?.avatar?.image_url ?? null;

                    page.cardTitle = 'Edit voucher';

                    cancellation_cutoff.month = Math.floor(Number(data.cancellation_cutoff) / 2592000);
                    cancellation_cutoff.day = Math.floor((Number(data.cancellation_cutoff) - (cancellation_cutoff.month * 2592000)) / 86400);

                    resolve();
                } catch (error) {
                    reject(error);
                }
            })();
        });
    }

    async function loadBusinessProfile() {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    const businessProfile = await store.dispatch('Auth/getBusinessProfile');
                    selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
                    selectedBusinessProfile.branch_id = businessProfile.branch_id;
                    selectedBusinessProfile.merchant_id = businessProfile.merchant_id;
                    voucher.merchant_id = businessProfile.merchant_id;

                    if (businessProfile.merchant_id) {
                        // Merchant context: auto-select merchant, scope = specific
                        voucher.merchant_scope = 'specific';
                        if (route.name === 'VoucherForm') {
                            voucher.merchant_list = [businessProfile.merchant_id];
                        }
                    } else {
                        // Corporate or Branch context: load merchants dropdown
                        await loadMerchantsForCorporate(businessProfile);
                    }
                    resolve();
                } catch (error) {
                    reject(error);
                }
            })();
        });
    }

    const merchantSearch = ref('');

    async function loadMerchantsForCorporate(businessProfile) {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    let rawList = [];

                    // Corporate context: load all merchants under corporate
                    if (!businessProfile.branch_id && businessProfile.corporate_id) {
                        try {
                            rawList = await store.dispatch('Merchant/read_all_corporate', businessProfile.corporate_id);
                        } catch {
                            store.commit('Merchant/add_filter', { filter: { corporate_id: businessProfile.corporate_id } });
                            await store.dispatch('Merchant/read');
                            rawList = store.state.Merchant.merchants;
                        }
                    } 
                    // Branch context: load merchants under branch
                    else if (businessProfile.branch_id) {
                        try {
                            rawList = await store.dispatch('Merchant/read_all_branch', businessProfile.branch_id);
                        } catch {
                            store.commit('Merchant/add_filter', { filter: { branch_id: businessProfile.branch_id } });
                            await store.dispatch('Merchant/read');
                            rawList = store.state.Merchant.merchants;
                        }
                    }

                    merchants.value = (rawList || []).map((merch) => ({ 
                        value: merch.id, 
                        text: merch.name,
                        branch_id: merch.branch_id || merch.branch?.id || 'unassigned',
                        branch_name: merch.branch?.name || merch.branch_name || 'General Branch'
                    }));

                    if (route.name === 'VoucherForm') {
                        if (voucher.merchant_scope === 'all_corporate' || !voucher.merchant_list || voucher.merchant_list.length === 0) {
                            voucher.merchant_list = merchants.value.map(m => m.value);
                        }
                        if (!voucher.merchant_id && merchants.value.length > 0) {
                            voucher.merchant_id = merchants.value[0].value;
                        }
                        // MEM-1287: scope mengikuti jumlah merchant hasil prefill
                        syncMerchantScope();
                    } else {
                        if (voucher.merchant_scope === 'all_corporate' && (!voucher.merchant_list || voucher.merchant_list.length === 0)) {
                            voucher.merchant_list = merchants.value.map(m => m.value);
                        }
                    }

                    resolve();
                } catch (error) {
                    reject(error);
                }
            })();
        });
    }

    const branchGroups = computed(() => {
        const groupsMap = {};

        merchants.value.forEach(m => {
            const branchKey = m.branch_id || 'unassigned';
            const branchName = m.branch_name || 'General Branch';

            if (!groupsMap[branchKey]) {
                groupsMap[branchKey] = {
                    branch_id: branchKey,
                    branch_name: branchName,
                    merchants: []
                };
            }
            groupsMap[branchKey].merchants.push(m);
        });

        return Object.values(groupsMap);
    });

    const filteredBranchGroups = computed(() => {
        const query = merchantSearch.value.trim().toLowerCase();
        if (!query) return branchGroups.value;

        return branchGroups.value.map(group => {
            const matchesBranch = group.branch_name.toLowerCase().includes(query);
            const matchingMerchants = group.merchants.filter(m => 
                matchesBranch || m.text.toLowerCase().includes(query)
            );

            if (matchingMerchants.length > 0) {
                return {
                    ...group,
                    merchants: matchingMerchants
                };
            }
            return null;
        }).filter(Boolean);
    });

    const totalMerchantCount = computed(() => merchants.value.length);
    const selectedMerchantCount = computed(() => voucher.merchant_list.length);
    const selectedMerchantsList = computed(() => {
        return merchants.value.filter(m => voucher.merchant_list.includes(m.value));
    });

    // MEM-1287: merchant_scope ditentukan dari jumlah merchant yang dipilih.
    //   > 1 merchant  -> 'all_corporate' (voucher milik corporate, dipakai bersama)
    //   0 / 1 merchant -> 'specific'     (voucher milik satu merchant; 0 akan ditolak validasi)
    function resolveMerchantScope(list) {
        return Array.isArray(list) && list.length > 1 ? 'all_corporate' : 'specific';
    }

    function syncMerchantScope() {
        voucher.merchant_scope = resolveMerchantScope(voucher.merchant_list);
    }

    function isMerchantSelected(merchantId) {
        return voucher.merchant_list.includes(merchantId);
    }

    function toggleMerchantSelection(merchantId) {
        const index = voucher.merchant_list.indexOf(merchantId);
        if (index > -1) {
            voucher.merchant_list.splice(index, 1);
        } else {
            voucher.merchant_list.push(merchantId);
        }
        if (voucher.merchant_list.length > 0) {
            voucher.merchant_id = voucher.merchant_list[0];
        } else {
            voucher.merchant_id = '';
        }
        syncMerchantScope();
    }

    function selectAllCorporateScope() {
        voucher.merchant_list = merchants.value.map(m => m.value);
        if (merchants.value.length > 0) {
            voucher.merchant_id = merchants.value[0].value;
        }
        syncMerchantScope();
    }

    function selectAllMerchants() {
        voucher.merchant_list = merchants.value.map(m => m.value);
        if (merchants.value.length > 0) {
            voucher.merchant_id = merchants.value[0].value;
        }
        syncMerchantScope();
    }

    function deselectAllMerchants() {
        voucher.merchant_list = [];
        voucher.merchant_id = '';
        syncMerchantScope();
    }

    function getBranchSelectedCount(group) {
        return group.merchants.filter(m => voucher.merchant_list.includes(m.value)).length;
    }

    function isBranchAllSelected(group) {
        if (!group.merchants || group.merchants.length === 0) return false;
        return group.merchants.every(m => voucher.merchant_list.includes(m.value));
    }

    function toggleBranchChecklist(group) {
        const allSelected = isBranchAllSelected(group);
        const branchMerchantIds = group.merchants.map(m => m.value);

        if (allSelected) {
            voucher.merchant_list = voucher.merchant_list.filter(id => !branchMerchantIds.includes(id));
        } else {
            branchMerchantIds.forEach(id => {
                if (!voucher.merchant_list.includes(id)) {
                    voucher.merchant_list.push(id);
                }
            });
        }

        if (voucher.merchant_list.length > 0) {
            voucher.merchant_id = voucher.merchant_list[0];
        } else {
            voucher.merchant_id = '';
        }
        syncMerchantScope();
    }

    async function loadMerchants() {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    const filter = {
                        branch_id: selectedBusinessProfile.branch_id
                    };

                    store.commit('Merchant/add_filter', { filter });
                    await store.dispatch('Merchant/read');
                    merchants.value = [...store.state.Merchant.merchants.map((merch) => ({ 'value': merch.id, 'text': merch.name }))];
                    resolve();
                } catch (error) {
                    reject(error);
                }
            })();
        });
    }

    function change(){

        const data = {...voucher}
        
        // Ensure product_service has correct default values to pass validation
        if (data.voucher_type === 'product_service') {
            data.discount_type = 'point';
        }
        
        // Map merchant_list to allowed_merchants for backend payload
        if (selectedBusinessProfile.merchant_id) {
            data.merchant_scope = 'specific';
            data.merchant_list = [selectedBusinessProfile.merchant_id];
            data.merchant_id = selectedBusinessProfile.merchant_id;
        } else {
            // MEM-1287: scope diturunkan dari jumlah merchant terpilih.
            // merchant_list TIDAK lagi ditimpa dengan seluruh merchant, agar pilihan user tetap utuh.
            data.merchant_list = [...(data.merchant_list || [])];
            data.merchant_scope = resolveMerchantScope(data.merchant_list);
            voucher.merchant_scope = data.merchant_scope;
            if ((!data.merchant_id || data.merchant_id === '') && data.merchant_list && data.merchant_list.length > 0) {
                data.merchant_id = data.merchant_list[0];
            }
            voucher.merchant_id = data.merchant_id;
        }
        data.allowed_merchants = data.merchant_list;

        if (data.expiration instanceof Date) {
            data.expiration = formatLocalDateTime(data.expiration);
        }

        if (data.cancelable) {
            const month = cancellation_cutoff.month * 2592000;
            const day = cancellation_cutoff.day * 86400;
            data.cancellation_cutoff = month + day;
        } else {
            data.cancellation_cutoff = 0;
        }

        validation.value = {};
        error.value = '';

        if (!data.name) {
            validation.value.name = ['The name field is required.'];
        } else if (data.name.length > 150) {
            validation.value.name = ['The name field must not be greater than 150 characters.'];
        }

        if (selectedBusinessProfile.merchant_id == null && !data.merchant_id) {
            validation.value.merchant_id = ['The merchant ID field is required.'];
        }

        if (data.point == null || data.point === '' || data.point < 0) {
            validation.value.point = ['The point needed field is required and cannot be negative.'];
        }

        if (route.name === 'VoucherForm' && !data.avatar) {
            validation.value.avatar = ['The voucher image field is required.'];
        }

        if (!data.description || data.description.trim() === '') {
            validation.value.description = ['The description field is required.'];
        }

        if (!data.expiration) {
            validation.value.expiration = ['The expiration date field is required.'];
        }

        if (selectedBusinessProfile.corporate_id != null && !data.member_tier_id) {
            validation.value.member_tier_id = ['The member tier field is required.'];
        }

        if (data.is_limited) {
            if (data.stock === null || data.stock === '' || Number(data.stock) < 1) {
                validation.value.stock = ['The stock quantity must be at least 1.'];
            }
        }

        if (data.voucher_type === 'credit_discount') {
            if (!data.discount_type) {
                validation.value.discount_type = ['The discount type field is required.'];
            }
            if (data.discount_value === '' || data.discount_value === null || data.discount_value === undefined) {
                validation.value.discount_value = ['The discount value field is required.'];
            } else if (Number(data.discount_value) < 1) {
                validation.value.discount_value = ['The discount value must be at least 1.'];
            } else if (data.discount_type === 'percentage' && Number(data.discount_value) > 100) {
                validation.value.discount_value = ['The discount value must not be greater than 100.'];
            }
            if (data.discount_type === 'percentage' && !data.max_discount) {
                validation.value.max_discount = ['The max discount field is required when percentage is selected.'];
            }
        }

        if (!selectedBusinessProfile.merchant_id) {
            if (!data.merchant_scope) {
                validation.value.merchant_scope = ['The merchant scope field is required.'];
            }
            if (data.merchant_scope === 'specific' && (!data.merchant_list || data.merchant_list.length === 0)) {
                validation.value.merchant_list = ['The merchant list field is required when scope is specific.'];
            }
        }

        if (Object.keys(validation.value).length > 0) {
            showToast({
                type: 'error',
                title: 'Validation Error',
                message: 'Please check the form. Some fields are missing or invalid.'
            });
            return;
        }

        page.isLoading = true;

        if(route.name != 'VoucherForm'){
            
            if (data.avatar == '') {
                delete data.avatar;
            }

            store.dispatch('Voucher/update', {data}).then(() => {
                showToast({
                    type: 'success',
                    title: 'Updated',
                    message: `Voucher has been updated successfully.`
                });
                router.push({
                    name: 'Voucher',
                })
            }).catch((err) => {
                let toastMessage = 'Failed to update voucher. Please check the form.';
                if (err.response?.status === 400) {
                    const errorData = typeof err.response.data.message === 'string' 
                        ? tryParseJson(err.response.data.message) 
                        : err.response.data.message;

                    if (typeof errorData === 'object' && errorData !== null && !Array.isArray(errorData)) {
                        validation.value = errorData;
                        toastMessage = "Please check the form. Some fields are missing or invalid.";
                    } else {
                        let errorMessages = [];
                        if (Array.isArray(errorData)) {
                            errorMessages = errorData.map(msg => `• ${msg}`);
                        } else {
                            errorMessages = [`• ${errorData}`];
                        }
                        error.value = errorMessages.join('\n');
                        toastMessage = error.value;
                    }
                } else if (err.response?.status === 403) {
                    error.value = err.response.data.message;
                    toastMessage = error.value;
                } else if (err.response?.data) {
                    const rawMsg = err.response.data.message || err.response.data.error;
                    try {
                        const parsed = typeof rawMsg === 'string' ? JSON.parse(rawMsg) : rawMsg;
                        validation.value = parsed;
                        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
                            toastMessage = "Please check the form. Some fields are missing or invalid.";
                        }
                    } catch {
                        error.value = rawMsg;
                        toastMessage = error.value;
                    }
                }

                showToast({
                    type: 'error',
                    title: 'Error',
                    message: toastMessage.replace('• ', '')
                });
            }).finally(() =>{
                page.isLoading = false
            });
        }else{
            store.dispatch('Voucher/create', {data}).then(() => {
                showToast({
                    type: 'success',
                    title: 'Created',
                    message: `Voucher has been created successfully.`
                });
                sessionStorage.setItem('newVoucherAdded', 'true');
                router.push({
                    name: 'Voucher'
                })
            }).catch((err) => {
                let toastMessage = 'Failed to create voucher. Please check the form.';
                if (err.response?.status === 400) {
                    const errorData = typeof err.response.data.message === 'string' 
                        ? tryParseJson(err.response.data.message) 
                        : err.response.data.message || err.response.data.error;

                    if (typeof errorData === 'object' && errorData !== null && !Array.isArray(errorData)) {
                        // It's a validation object
                        validation.value = errorData;
                        toastMessage = "Please check the form. Some fields are missing or invalid.";
                    } else {
                        let errorMessages = [];
                        if (Array.isArray(errorData)) {
                            errorMessages = errorData.map(msg => `• ${msg}`);
                        } else {
                            errorMessages = [`• ${errorData}`];
                        }
                        error.value = errorMessages.join('\n');
                        toastMessage = error.value;
                    }
                } else if (err.response?.status === 403) {
                    error.value = err.response.data.message;
                    toastMessage = error.value;
                } else if (err.response?.data) {
                    const rawMsg = err.response.data.message || err.response.data.error;
                    try {
                        const parsed = typeof rawMsg === 'string' ? JSON.parse(rawMsg) : rawMsg;
                        validation.value = parsed;
                        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && Object.keys(parsed).length > 0) {
                            toastMessage = "Please check the form. Some fields are missing or invalid.";
                        }
                    } catch {
                        error.value = rawMsg;
                        toastMessage = error.value;
                    }
                }

                showToast({
                    type: 'error',
                    title: 'Error',
                    message: toastMessage.replace('• ', '')
                });
            }).finally(() => {
                page.isLoading = false;
            });
        }
    }

    function tryParseJson(str) {
        try {
            return JSON.parse(str);
        } catch {
            return str;
        }
    }

    watch(
    () => voucher.point,
    (newValue) => {
        validationPoint.value = []; // Reset validation messages

        if (!newValue) {
        return;
        }

        if (typeof newValue === 'string') {
        // Test for non-numeric characters
        const hasNonNumeric = /\D/.test(newValue);

        // Check if input contains non-numeric characters
        if (hasNonNumeric) {
            validationPoint.value.push('Input must be numeric.');
        }

        // Limit to 10 digits
        if (newValue.length > 10) {
            validationPoint.value.push('Input must not exceed 10 digits.');
            nextTick(() => {
            voucher.point = newValue.slice(0, 10);
            });
        }
        }
    }
    );

    watch(
        () => tempExpiration.value,
        (val) => {
            voucher.expiration = val;
        }
    )

        function formatLocalDateTime(dateObj) {
        if (!dateObj) return '';
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const seconds = String(dateObj.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }

    function imageHandling(img) {
        voucher.avatar = img
    }

    function cancel() {
        router.push({ name: 'Voucher' });
    }

    function handleNumericPaste(e) {
        const pasted = e.clipboardData.getData('text/plain').replace(/[^0-9]/g, '');
        document.execCommand('insertText', false, pasted);
    }

    return {
        page,
        voucher,
        validation,
        router,
        options,
        change,
        merchants,
        loadMerchants,
        selectedBusinessProfile,
        cancellation_cutoff,
        image,
        imageHandling,
        isLoaded,
        validationPoint,
        hasValidationErrors,
        cancel,
        error,
        closeDatePicker,
        handleNumericPaste,
        tempExpiration,
        showOkExpiration,
        showDiscountValueTip,
        displayDiscountValue,
        displayMinTransaction,
        displayMaxDiscount,
        validatePriceInput,
        preventInvalidInput,
        handlePricePaste,
        isScrolled,
        showToast,
        merchantSearch,
        branchGroups,
        filteredBranchGroups,
        totalMerchantCount,
        selectedMerchantCount,
        isMerchantSelected,
        toggleMerchantSelection,
        selectAllCorporateScope,
        selectAllMerchants,
        deselectAllMerchants,
        getBranchSelectedCount,
        isBranchAllSelected,
        toggleBranchChecklist,
        isEligibleMerchantsCollapsed,
        selectedMerchantsList
    }
}
}
</script>

<style scoped>
/* Page Layout */
.page-container {
    width: 100%;
    min-height: calc(100vh - 64px);
    background-color: #f1f4f9;
    padding: 24px 40px;
}

.content-wrapper {
    max-width: 1240px;
    margin: 0 auto;
}

/* Page Header Wrapper */
.page-header-wrapper {
    position: sticky;
    top: 0;
    z-index: 99;
    margin: -24px -40px 16px -40px;
    padding: 24px 40px 0 40px;
    background: rgba(241, 244, 249, 0);
    border-bottom: 1px solid rgba(0, 0, 0, 0);
    box-shadow: 0 10px 20px 5px rgba(241, 244, 249, 0);
    backdrop-filter: blur(0px);
    -webkit-backdrop-filter: blur(0px);
    transition: background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease, -webkit-backdrop-filter 0.3s ease;
}

.page-header-wrapper.is-scrolled {
    /* Glassmorphism Effect */
    background: rgba(241, 244, 249, 0.85);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.03);
    box-shadow: 0 10px 20px 5px rgba(241, 244, 249, 0.5);
}

/* Page Header */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    padding-bottom: 16px;
}

.page-title {
    font-size: 26px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.5px;
    margin: 0;
}

.page-subtitle {
    color: #64748b;
    font-size: 14px;
    margin: 4px 0 0 0;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
}

/* Buttons */
.btn-cancel {
    padding: 10px 24px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}

.btn-cancel:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn-save {
    padding: 10px 24px;
    background: var(--primary-color, #F93E3E);
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn-save:hover {
    filter: brightness(0.9);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.15);
}

.btn-save:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
}

/* Grid Layout */
.main-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 24px;
    align-items: start;
}

/* Content Cards */
.content-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    padding: 28px 32px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.section-desc {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 20px;
}

.section-divider {
    border: none;
    height: 2px;
    background: linear-gradient(70deg, transparent, #64748b, transparent);
    margin: 28px 2px;
}

/* Form Styling */
.form-grid {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.form-item label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.form-item.full {
    width: 100%;
}

.form-item.half {
    width: 50%;
}

/* A row of two or more side-by-side sub-items */
.form-item.form-row {
    display: flex;
    gap: 16px;
}

.form-sub-item {
    flex: 1;
    min-width: 0;
}

.form-sub-item label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.required {
    color: #ef4444;
}

.error-text {
    color: #ef4444;
    font-size: 12px;
    margin-top: 5px;
    font-weight: 500;
}

.error-box-inline {
    display: flex;
    align-items: flex-start;
    background-color: #fef2f2;
    border: 1px solid #f87171;
    color: #b91c1c;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 12px;
    font-weight: 500;
}
.error-box-inline i {
    color: #ef4444;
    margin-top: 2px;
}

.modern-select {
    width: 100%;
    padding: 11px 14px !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    color: #0f172a !important;
    background-color: #f8fafc !important;
    margin: 0 !important;
    transition: all 0.2s;
    appearance: auto;
}

.helper-text {
    font-size: 10px;
    color: #a3acba;
    margin-top: 4px;
    font-style: italic;
}

/* Ensure no wrapper margins break Flex alignment */
:deep(.input-wrapper) {
    margin: 0 !important;
}


.modern-select:focus {
    border-color: var(--primary-color, #F93E3E) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06) !important;
    outline: none !important;
}

/* Override o-input-text & o-select focus color to match BenefitForm */
:deep(input),
:deep(textarea) {
    width: 100%;
    padding: 11px 14px !important;
    border: 1.5px solid #e2e8f0 !important;
    border-radius: 8px !important;
    font-size: 14px !important;
    color: #0f172a !important;
    background-color: #f8fafc !important;
    transition: all 0.2s;
}

:deep(input:focus),
:deep(textarea:focus) {
    border-color: var(--primary-color, #F93E3E) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06) !important;
    outline: none !important;
}

:deep(input:disabled),
:deep(input[readonly]) {
    background-color: #f1f5f9 !important;
    color: #94a3b8 !important;
    cursor: not-allowed;
}

/* Lock Tier Toggle Card */
.lock-tier-card {
    display: flex !important;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    background-color: #f8fafc;
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
    width: 100%;
}

.lock-tier-card:hover {
    border-color: #cbd5e1;
    background-color: #f1f5f9;
}

.lock-tier-card.active {
    border-color: var(--primary-color, #F93E3E);
    background-color: var(--secondary-color, #FEE9E9);
}

.lock-tier-info {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-grow: 1;
}

.lock-icon {
    font-size: 18px;
    color: #94a3b8;
    transition: color 0.2s;
    flex-shrink: 0;
}

.lock-tier-card.active .lock-icon {
    color: var(--primary-color, #F93E3E);
}

.lock-tier-title {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.lock-tier-desc {
    font-size: 12px;
    color: #64748b;
    margin-top: 2px;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
}

/* Toggle Switch */
.lock-tier-toggle {
    position: relative;
    width: 44px;
    height: 24px;
    flex-shrink: 0;
}

.toggle-input {
    opacity: 0 !important;
    width: 0 !important;
    height: 0 !important;
    position: absolute;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    box-shadow: none !important;
    background: none !important;
}

.toggle-slider {
    position: absolute;
    inset: 0;
    background: #cbd5e1;
    border-radius: 99px;
    transition: background 0.2s;
    cursor: pointer;
}

.toggle-slider::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    left: 3px;
    top: 3px;
    background: white;
    border-radius: 50%;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.toggle-input:checked + .toggle-slider {
    background: var(--primary-color, #F93E3E);
}

.toggle-input:checked + .toggle-slider::before {
    transform: translateX(20px);
}

.visual-upload-section {
    background: white;
}

/* Global Loader */
.global-loader {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

/* Responsive */
@media (max-width: 1024px) {
    .main-grid {
        grid-template-columns: minmax(0, 1fr);
    }
    .form-item.half {
        width: 100%;
    }
}

@media (max-width: 640px) {
    .page-container {
        padding: 12px 8px;
    }
    .page-header-wrapper {
        margin: -12px -8px 16px -8px;
        padding: 12px 8px 0 8px;
    }
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        padding-bottom: 12px;
    }
    .header-left {
        width: 100%;
        padding-right: 48px;
    }
    .page-title {
        font-size: 20px;
        margin: 0;
    }
    .page-subtitle {
        display: none;
    }
    .header-actions {
        width: 100%;
        gap: 8px;
        justify-content: flex-start;
    }
    .btn-cancel, .btn-save {
        padding: 8px 16px;
        font-size: 13px;
    }
    .form-item.form-row {
        flex-direction: column;
    }
    .content-card {
        padding: 16px 12px;
    }
}

/* Reset generic input overrides inside vue-select */
:deep(.vs__search),
:deep(.vs__search:focus) {
    width: auto !important;
    flex: 1 !important;
    padding: 4px 6px !important;
    border: none !important;
    border-radius: 0 !important;
    background-color: transparent !important;
    box-shadow: none !important;
    font-size: 14px !important;
    color: #0f172a !important;
    margin: 0 !important;
    outline: none !important;
}

/* Vue-Select Modern Overrides */
:deep(.modern-v-select) {
    --vs-border-color: #cbd5e1;
    --vs-border-radius: 8px;
    --vs-font-size: 14px;
    --vs-colors--light: #64748b;
}

:deep(.modern-v-select .vs__dropdown-toggle) {
    background-color: #f8fafc;
    border: 1px solid var(--vs-border-color);
    border-radius: var(--vs-border-radius);
    padding: 6px 4px;
    min-height: 42px;
    margin: 0 !important;
    transition: all 0.2s ease;
}

:deep(.modern-v-select.vs--open .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-toggle:focus-within) {
    background-color: #ffffff;
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
}

:deep(.modern-v-select .vs__search::placeholder),
:deep(.modern-v-select .vs__dropdown-toggle),
:deep(.modern-v-select .vs__dropdown-menu) {
    font-family: inherit;
    font-size: 14px;
    color: #334155;
}

:deep(.modern-v-select .vs__clear),
:deep(.modern-v-select .vs__open-indicator) {
    fill: #94a3b8;
}

:deep(.modern-v-select .vs__dropdown-menu) {
    border-color: #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border-radius: 8px;
    margin-top: 4px;
    padding: 4px 0;
}

:deep(.modern-v-select .vs__dropdown-option) {
    padding: 8px 16px;
    color: #334155;
}

:deep(.modern-v-select .vs__dropdown-option--highlight) {
    background: var(--primary-color, #F93E3E);
    color: #ffffff;
}

/* Override rich text editor height to ensure good UX */
:deep(.o-text-editor__content) {
  min-height: 150px;
  max-height: 50vh;
  overflow-y: auto;
}

.date-selection-tip.discount-value-tip {
    left: -10px;
    right: auto;
    bottom: 100%;
    margin-bottom: 5px;
    width: max-content;
    max-width: 260px;
    white-space: normal;
    align-items: flex-start;
    text-transform: none;
    letter-spacing: normal;
}

.date-selection-tip.discount-value-tip::before {
    left: 12px;
    right: auto;
    bottom: -4px;
    border-width: 4px 4px 0 4px;
    border-color: #1e293b transparent transparent transparent;
}

/* Override rich text editor height to ensure good UX */
:deep(.o-text-editor__content) {
  min-height: 200px;
  max-height: 60vh;
  overflow-y: auto;
}

/* Eligible Merchants Collapsible Section Styles */
.merchant-collapsible-section {
    position: relative;
}

.merchant-section-header {
    cursor: pointer;
    user-select: none;
    padding: 8px 0;
    transition: opacity 0.2s ease;
}

.merchant-section-header:hover {
    opacity: 0.9;
}

.section-header-icon {
    font-size: 18px;
    color: var(--primary-color, #F93E3E);
}

.scope-summary-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 9999px;
    color: #ffffff;
    background-color: var(--primary-color, #F93E3E);
}

.selected-merchant-items-container {
    max-height: 220px;
    overflow-y: auto;
    padding-right: 4px;
}

.selected-merchant-summary-item {
    font-size: 12px;
    color: #1e293b;
}

.theme-check-icon {
    color: var(--primary-color, #F93E3E);
    font-size: 12px;
    flex-shrink: 0;
}

.merchant-summary-name {
    font-weight: 500;
}

.merchant-badge-pill {
    font-size: 12px;
    font-weight: 600;
    color: var(--primary-color, #F93E3E);
    background-color: rgba(249, 62, 62, 0.08);
    border: 1px solid rgba(249, 62, 62, 0.2);
    padding: 6px 14px;
    border-radius: 9999px;
}

.btn-collapse-toggle {
    background: transparent;
    border: none;
    outline: none;
    padding: 6px;
    font-size: 14px;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-collapse-toggle .fa-chevron-down {
    transition: transform 0.25s ease;
}

.btn-collapse-toggle .fa-chevron-down.rotated {
    transform: rotate(180deg);
}

.merchant-control-bar {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 16px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
}

.merchant-search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
    max-width: 320px;
}

.merchant-search-wrapper .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    color: #94a3b8;
    font-size: 13px;
    pointer-events: none;
}

.merchant-search-input {
    width: 100%;
    padding: 8px 12px 8px 36px !important;
    padding-left: 36px !important;
    font-size: 13px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background-color: #ffffff;
    color: #0f172a;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.merchant-search-input:focus {
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 3px rgba(249, 62, 62, 0.1);
}

.merchant-action-group {
    display: flex;
    gap: 8px;
}

.merchant-btn {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
}

.merchant-btn-primary {
    background-color: #ffffff;
    color: var(--primary-color, #F93E3E);
    border: 1px solid var(--primary-color, #F93E3E);
}

.merchant-btn-primary:hover {
    background-color: var(--primary-color, #F93E3E);
    color: #ffffff;
}

.merchant-btn-secondary {
    background-color: #ffffff;
    color: #64748b;
    border: 1px solid #cbd5e1;
}

.merchant-btn-secondary:hover {
    background-color: #f1f5f9;
    color: #334155;
}

.branch-card-container {
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 12px;
}

.branch-card-header {
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.branch-header-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.branch-icon {
    color: var(--primary-color, #F93E3E);
    font-size: 14px;
}

.branch-title-text {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
}

.branch-count-badge {
    font-size: 11px;
    font-weight: 600;
    background-color: #e2e8f0;
    color: #475569;
    padding: 2px 8px;
    border-radius: 9999px;
}

.branch-toggle-btn {
    background: transparent;
    border: none;
    color: var(--primary-color, #F93E3E);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
}

.branch-toggle-btn:hover {
    text-decoration: underline;
}

.branch-card-body {
    padding: 14px 16px;
}

.merchant-grid-layout {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 10px;
}

.merchant-checkbox-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background-color: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease;
    margin-bottom: 0;
}

.merchant-checkbox-card:hover {
    border-color: var(--primary-color, #F93E3E);
    background-color: #f8fafc;
}

.merchant-checkbox-card.merchant-card-selected {
    border-color: var(--primary-color, #F93E3E);
    background-color: rgba(249, 62, 62, 0.04);
}

.merchant-input-checkbox {
    width: 16px;
    height: 16px;
    accent-color: var(--primary-color, #F93E3E);
    cursor: pointer;
    flex-shrink: 0;
}

.merchant-info-content {
    overflow: hidden;
}

.merchant-name-text {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.merchant-id-text {
    font-size: 11px;
    color: #64748b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.merchant-empty-state {
    text-align: center;
    padding: 32px 16px;
    background-color: #f8fafc;
    border-radius: 8px;
    color: #64748b;
    font-size: 13px;
}

.merchant-empty-state .empty-icon {
    font-size: 24px;
    margin-bottom: 8px;
    display: block;
    color: #94a3b8;
}
</style>